import axios, { AxiosError } from 'axios';
import { useMemo } from 'react';
import { ContactDetailType } from 'components/VerifiableContactDetailField/types';
import urlJoin from 'url-join';

export interface VerifiableContactDetailApi {
  sendCode: (contactDetail: string) => Promise<boolean>;
  verifyCode: (contactDetail: string, code: string) => Promise<ApiResponse>;
}

export interface ApiResponse {
  hash?: string;
  verifiedDetail?: string;
  message?: string;
  result?: string;
  successful?: boolean;
}

interface VerifyContactDetailRequest {
  contactMethod: 'Email' | 'SMS';
  purpose: 'VerifyEmail' | 'VerifyMobile';
  contactDetail: string;
  code?: string;
}

// Both the sendCode and verifyCode use the same method body; sendCode just doesn't add the `code` property.
const buildRequestBody = (
  type: ContactDetailType,
  contactDetail: string,
  code?: string
): VerifyContactDetailRequest => {
  return {
    ...getMethodAndPurpose(),
    contactDetail,
    code,
  };

  function getMethodAndPurpose(): Pick<VerifyContactDetailRequest, 'contactMethod' | 'purpose'> {
    if (type === 'mobile') {
      return {
        contactMethod: 'SMS',
        purpose: 'VerifyMobile',
      };
    }

    return {
      contactMethod: 'Email',
      purpose: 'VerifyEmail',
    };
  }
};

const createApi = (
  type: ContactDetailType,
  endpoint: string,
  sendCodePath: string,
  verifyCodePath: string,
  authorization?: string
): VerifiableContactDetailApi => {
  const buildHeader = () => {
    const r = {
      headers: {},
    };
    if (authorization) {
      r.headers['Authorization'] = authorization;
    }
    return r;
  };

  const sendCode = async (contactDetail: string) => {
    const body = buildRequestBody(type, contactDetail);

    try {
      await axios.post<ApiResponse>(urlJoin(endpoint, sendCodePath), body, buildHeader());

      return true;
    } catch (err) {
      throw new Error('Could not send verification code to ' + contactDetail);
    }
  };

  const verifyCode = async (contactDetail: string, code: string): Promise<ApiResponse> => {
    const body = buildRequestBody(type, contactDetail, code);
    try {
      const response = await axios.post<ApiResponse>(urlJoin(endpoint, verifyCodePath), body, buildHeader());
      return response?.data;
    } catch (err) {
      // We consider a 400 to represent that the code did not match
      if ((err as AxiosError<string>).response?.status === 400) {
        throw new Error('Code is not valid. Please try again');
      }

      // For anything else we'll just show the error that the API returned, or a default.
      const errorMessage =
        typeof (err as AxiosError<string>).response?.data === 'string'
          ? (err as AxiosError<string>).response?.data
          : 'There was a problem verifying the code. Please try again.';

      throw new Error(errorMessage);
    }
  };

  return { sendCode, verifyCode };
};

export const useVerifiableContactDetailApi = (
  type: ContactDetailType,
  endpoint: string,
  sendCodePath = '/2FA/SendCode',
  verifyCodePath = '/2FA/VerifyCode',
  authorization?: string
): VerifiableContactDetailApi => {
  return useMemo(
    () => createApi(type, endpoint, sendCodePath, verifyCodePath, authorization),
    [type, endpoint, sendCodePath, verifyCodePath]
  );
};
